import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, Camera, Video, MessageSquare, Users, TrendingUp, RefreshCw } from "lucide-react";
import api from "../lib/api";

const _fmt = (n) => (n ?? 0).toLocaleString();
const _fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.round((now - d) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

const _kindIcon = (kind) => {
  if (kind === "photo") return <Camera className="w-3.5 h-3.5" />;
  if (kind === "video") return <Video className="w-3.5 h-3.5" />;
  return <MessageSquare className="w-3.5 h-3.5" />;
};

/** 30-day sparkline as an inline SVG — no chart library needed. */
function Sparkline({ series }) {
  const w = 480, h = 60, pad = 4;
  if (!series || series.length === 0) {
    return <div className="text-navy-700/40 text-xs">No activity in the last 30 days.</div>;
  }
  const max = Math.max(1, ...series.map((p) => p.count));
  const stepX = (w - pad * 2) / Math.max(1, series.length - 1);
  const pts = series.map((p, i) => {
    const x = pad + i * stepX;
    const y = h - pad - (p.count / max) * (h - pad * 2);
    return [x, y];
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${h} L${pts[0][0].toFixed(1)},${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16">
      <path d={area} fill="rgba(232, 132, 107, 0.15)" />
      <path d={line} fill="none" stroke="#E8846B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill="#E8846B" />
      ))}
    </svg>
  );
}

/** One small counter box for the "last 24h" strip. */
function Counter({ icon: Icon, label, value, tone = "coral" }) {
  const bg = tone === "coral" ? "bg-coral-100 text-coral-500"
    : tone === "sky" ? "bg-sky-100 text-sky-600"
      : "bg-emerald-100 text-emerald-600";
  return (
    <div className="flex items-center gap-3 bg-cream-100 rounded-xl p-3">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${bg}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-navy-700 font-bold text-lg leading-none">{_fmt(value)}</div>
        <div className="text-navy-700/50 text-[11px] uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

export default function UploadAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/upload-analytics");
      setData(r.data);
    } catch (e) {
      // Panel is optional — silently ignore errors so we don't break the rest of the admin page.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 20 s while enabled.
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [auto, load]);

  const totals = useMemo(() => {
    if (!data) return null;
    return data.hosts.reduce(
      (acc, h) => ({
        events: acc.events + h.events,
        uploads: acc.uploads + h.uploads,
        photos: acc.photos + h.photos,
        videos: acc.videos + h.videos,
      }),
      { events: 0, uploads: 0, photos: 0, videos: 0 },
    );
  }, [data]);

  if (loading) return null;
  if (!data) return null;

  return (
    <div data-testid="admin-upload-analytics" className="bg-white rounded-3xl soft-shadow p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-navy-700 text-sm uppercase tracking-wider flex items-center gap-2">
            <Activity className="w-4 h-4 text-coral-300" /> Live upload analytics
          </h3>
          <p className="text-xs text-navy-700/50 mt-1">
            Who&apos;s using GalerieCop right now, and how much.
            {auto && <span className="ml-2 text-coral-500">Auto-refreshing every 20 s</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAuto((a) => !a)}
            data-testid="admin-analytics-toggle-auto"
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${
              auto ? "bg-coral-300 text-white border-coral-300"
                : "bg-white text-navy-700 border-navy-700/20 hover:bg-cream-100"
            }`}
          >
            {auto ? "Auto ON" : "Auto OFF"}
          </button>
          <button
            onClick={load}
            data-testid="admin-analytics-refresh"
            className="text-xs font-semibold rounded-full px-3 py-1.5 border border-navy-700/20 text-navy-700 hover:bg-cream-100 inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Last 24h counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Counter icon={TrendingUp} label="Uploads (1h)" value={data.today.uploads_1h} tone="coral" />
        <Counter icon={TrendingUp} label="Uploads (24h)" value={data.today.uploads_24h} tone="coral" />
        <Counter icon={Camera} label="Photos (24h)" value={data.today.photos_24h} tone="sky" />
        <Counter icon={Video} label="Videos (24h)" value={data.today.videos_24h} tone="sky" />
        <Counter icon={MessageSquare} label="Messages (24h)" value={data.today.messages_24h} tone="sky" />
        <Counter icon={Users} label="New signups (24h)" value={data.today.signups_24h} tone="emerald" />
        <Counter icon={Activity} label="New events (24h)" value={data.today.events_24h} tone="emerald" />
      </div>

      {/* 30-day sparkline */}
      <div>
        <div className="text-xs uppercase tracking-wider text-navy-700/50 mb-1">
          Uploads · last 30 days
        </div>
        <Sparkline series={data.activity_by_day} />
      </div>

      {/* Host table */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-navy-700/50">
            Hosts by upload volume
          </div>
          {totals && (
            <div className="text-xs text-navy-700/60">
              {_fmt(data.hosts.length)} hosts · {_fmt(totals.events)} events · {_fmt(totals.uploads)} uploads
            </div>
          )}
        </div>
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-navy-700/50 border-b border-cream-200">
                <th className="text-left py-2 pr-3">Host</th>
                <th className="text-right py-2 px-3">Events</th>
                <th className="text-right py-2 px-3">Uploads</th>
                <th className="text-right py-2 px-3">Photos</th>
                <th className="text-right py-2 px-3">Videos</th>
                <th className="text-right py-2 px-3">Messages</th>
                <th className="text-right py-2 pl-3">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-100">
              {data.hosts.map((h) => (
                <tr key={h.host_id} data-testid={`admin-host-row-${h.host_id}`} className="hover:bg-cream-50">
                  <td className="py-2 pr-3 min-w-[220px]">
                    <div className="font-medium text-navy-700 truncate">{h.name}</div>
                    <div className="text-navy-700/50 text-xs truncate">{h.email}</div>
                  </td>
                  <td className="text-right py-2 px-3 text-navy-700 tabular-nums">{_fmt(h.events)}</td>
                  <td className="text-right py-2 px-3 font-bold text-navy-700 tabular-nums">{_fmt(h.uploads)}</td>
                  <td className="text-right py-2 px-3 text-navy-700/70 tabular-nums">{_fmt(h.photos)}</td>
                  <td className="text-right py-2 px-3 text-navy-700/70 tabular-nums">{_fmt(h.videos)}</td>
                  <td className="text-right py-2 px-3 text-navy-700/70 tabular-nums">{_fmt(h.messages)}</td>
                  <td className="text-right py-2 pl-3 text-navy-700/60 text-xs whitespace-nowrap">{_fmtTime(h.last_upload)}</td>
                </tr>
              ))}
              {data.hosts.length === 0 && (
                <tr><td colSpan={7} className="text-center text-navy-700/40 py-6 text-sm">No hosts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent uploads + top guests side by side */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-navy-700/50 mb-2">
            Latest uploads (live)
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-cream-100 border border-cream-200 rounded-xl">
            {data.recent_uploads.map((u) => (
              <div key={u.id} className="p-3 flex items-center gap-3 text-sm">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                  u.kind === "photo" ? "bg-coral-100 text-coral-500"
                    : u.kind === "video" ? "bg-sky-100 text-sky-600"
                      : "bg-cream-200 text-navy-700/70"
                }`}>
                  {_kindIcon(u.kind)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-navy-700 truncate">
                    <span className="text-navy-700/70">{u.uploader_name}</span> · {u.event_name}
                  </div>
                  <div className="text-navy-700/50 text-xs">{u.event_slug || "—"}</div>
                </div>
                <div className="text-xs text-navy-700/50 whitespace-nowrap">{_fmtTime(u.created_at)}</div>
              </div>
            ))}
            {data.recent_uploads.length === 0 && (
              <div className="p-6 text-center text-navy-700/40 text-sm">No uploads yet.</div>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-navy-700/50 mb-2">
            Top guest uploaders
          </div>
          <div className="border border-cream-200 rounded-xl divide-y divide-cream-100">
            {data.top_guests.map((g) => (
              <div key={g.name} className="p-3 flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <div className="font-medium text-navy-700 truncate">{g.name}</div>
                  <div className="text-navy-700/50 text-xs">
                    {_fmt(g.distinct_events)} event{g.distinct_events === 1 ? "" : "s"} · last {_fmtTime(g.last_upload)}
                  </div>
                </div>
                <div className="text-navy-700 font-bold tabular-nums text-lg">{_fmt(g.uploads)}</div>
              </div>
            ))}
            {data.top_guests.length === 0 && (
              <div className="p-6 text-center text-navy-700/40 text-sm">No guest uploads yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
